// 🔧 volunteer_opportunities.js
// ✅ Purpose: Public-facing page for users to view and sign up for available volunteer time slots
// ✅ Features:
// - Lists all available time slots (not yet full)
// - Groups by start time, sorted ascending
// - Within each start time group, lists activities alphabetically
// - Shows volunteers needed vs assigned
// - Allows user to select an available slot to volunteer for
// - Saves user to slot's assignedUserIds in Firestore
// - Shows success notification for 20 seconds

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import { formatDateTime } from "../../utils/date_format";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { Link } from "react-router-dom";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const VolunteerOpportunities = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState(null);
  const [calendarDetails, setCalendarDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ready && eventId) trackEvent({ action: GA_EVENTS.VOLUNTEER_OPPORTUNITIES_VIEWED, parameters: { event_slug: eventslug, event_id: eventId, source_page: window.location.pathname } });
  }, [ready, eventId, eventslug]);

  useEffect(() => {
    if (!eventId) return;

    const loadOpportunities = async () => {
      const snap = await getDocs(collection(db, "events", eventId, "volunteers"));
      const all = [];
      for (const docSnap of snap.docs) {
        const activity = { id: docSnap.id, ...docSnap.data() };
        const slotSnap = await getDocs(collection(db, "events", eventId, "volunteers", docSnap.id, "timeSlots"));
        for (const slotDoc of slotSnap.docs) {
          const slot = slotDoc.data();
          const startTime = slot.startTime || slot.startDateTime;
          const endTime = slot.endTime || slot.endDateTime;
          const assignedUsers = Array.isArray(slot.assignedUsers)
            ? slot.assignedUsers
            : (slot.assignedUserIds || []).map((uid) => ({ uid }));

          all.push({
            ...slot,
            assignedUsers,
            volunteersNeeded: Number(
              slot.volunteersNeeded ?? slot.requiredVolunteers ?? 0
            ),
            startTime: startTime?.toDate?.() || startTime,
            endTime: endTime?.toDate?.() || endTime,
            activityTitle: activity.title,
            activityId: activity.id,
            slotId: slotDoc.id,
          });
        }
      }
      all.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setSlots(all);
    };

    loadOpportunities();
  }, [eventId]);

  const grouped = {};
  slots.forEach((s) => {
    const key = formatDateTime(s.startTime);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  });

  const handleSignup = async (slot) => {
    if (!user || !eventId || submitting) return;

      const alreadyAssigned = (
        slot.assignedUsers || []
      ).some(
        (assignedUser) => assignedUser.uid === user.uid
      );

      if (alreadyAssigned) {
        showStatus(setStatus, {
        type: "warning",
        message: "You are already signed up for this volunteer slot.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const ref = doc(
        db,
        "events",
        eventId,
        "volunteers",
        slot.activityId,
        "timeSlots",
        slot.slotId
      );

      const assignedUsers = [
        ...(slot.assignedUsers || []),
        {
          uid: user.uid,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
        },
      ];

      await updateDoc(ref, {
        assignedUsers,

        // temporary legacy support
        assignedUserIds: assignedUsers.map(
          (assignedUser) => assignedUser.uid
        ),
      });

      await httpsCallable(functions, "sendVolunteerSignupEmail")({
        to: user.email,
        eventId,
        activityId: slot.activityId,
        slotId: slot.slotId,
      });

      setCalendarDetails({
        activityTitle: slot.activityTitle,
        startTime: slot.startTime,
        endTime: slot.endTime,
      });

      // 📊 Google Analytics
      trackEvent({
        action: GA_EVENTS.VOLUNTEER_SIGNUP_SUBMITTED,
        parameters: {
          activity: slot.activityTitle,
        },
      });

      showStatus(setStatus, {
        type: "success",
        message: `You are signed up for '${slot.activityTitle}'.`,
      });

      setSlots((prev) =>
        prev.map((currentSlot) =>
          currentSlot.slotId === slot.slotId &&
          currentSlot.activityId === slot.activityId
            ? { ...currentSlot, assignedUsers }
            : currentSlot
        )
      );

    } catch (err) {
      logger.error("❌ Volunteer signup failed:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Unable to sign up for this volunteer slot.",
      });
    } finally {
      setSubmitting(false);
    }
  }; 

  function generateGoogleCalendarLink({ activityTitle, startTime, endTime }) {
    const title = encodeURIComponent(activityTitle);
    const start = formatForGoogle(new Date(startTime));
    const end = formatForGoogle(new Date(endTime));
    const details = encodeURIComponent("Your volunteer shift.");
    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}`;
  }
  
  function formatForGoogle(date) {
    return date.toISOString().replace(/[-:]|\.\d{3}/g, "").slice(0, 15) + "Z";
  }
  
  function downloadICS({ activityTitle, startTime, endTime }) {
    const blob = new Blob(
      [
        `BEGIN:VCALENDAR
        VERSION:2.0
        BEGIN:VEVENT
        SUMMARY:${activityTitle}
        DTSTART:${formatForGoogle(new Date(startTime))}
        DTEND:${formatForGoogle(new Date(endTime))}
        DESCRIPTION:Volunteer shift
        STATUS:CONFIRMED
        END:VEVENT
        END:VCALENDAR`
      ],
      { type: "text/calendar;charset=utf-8" }
    );
  
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${activityTitle}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading volunteer opportunities..." />;
  }

  const volunteerOpportunitySeo = {
    ...PUBLIC_PAGE_SEO.volunteerOpportunities,
    path: `/${eventslug}/volunteer_opportunities`,
    noindex: !isEventPublic(eventDoc),
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: eventDoc?.name || "Event", path: `/${eventslug}/event` },
      { name: "Volunteer Opportunities", path: `/${eventslug}/volunteer_opportunities` },
    ],
  };

  return (
    <>
      <SeoHead {...volunteerOpportunitySeo} />

      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "1100px" }}>
          <div className="text-center mb-4">
            <h1 className="display-5 fw-bold mb-2">
              Volunteer Opportunities
            </h1>

            <p className="lead text-muted mb-0">
              Help support {eventDoc?.name || "this event"} by signing up for an available shift.
            </p>
          </div>

          {status?.message && (
            <div
              role="alert"
              className={`alert ${
                status.type === "success"
                  ? "alert-success"
                  : status.type === "warning"
                  ? "alert-warning"
                  : status.type === "error"
                  ? "alert-danger"
                  : "alert-info"
              }`}
            >
              {status.message}
            </div>
          )}

          {calendarDetails && (
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <strong>📅 Add Your Shift to Your Calendar</strong>
              </div>

              <div className="card-body">
                <p className="mb-3">
                  <strong>{calendarDetails.activityTitle}</strong>
                  <br />
                  {formatDateTime(calendarDetails.startTime)} –{" "}
                  {formatDateTime(calendarDetails.endTime)}
                </p>

                <div className="d-flex gap-2 flex-wrap">
                  <a
                    className="btn btn-outline-primary btn-sm"
                    href={generateGoogleCalendarLink(calendarDetails)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Add to Google Calendar
                  </a>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => downloadICS(calendarDetails)}
                  >
                    Download .ics
                  </button>

                  <Link
                    className="btn btn-outline-dark btn-sm"
                    to={`/${eventslug}/volunteering`}
                  >
                    View My Duties
                  </Link>
                </div>
              </div>
            </div>
          )}

          {Object.keys(grouped).length === 0 ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <h4 className="mb-2">All Volunteer Slots Are Filled</h4>

                <p className="text-muted mb-0">
                  Thank you for supporting the event. Please check back later if more opportunities are added.
                </p>
              </div>
            </div>
          ) : (
            Object.keys(grouped).map((timeKey) => (
              <div key={timeKey} className="card shadow-sm mb-4">
                <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                  <div>
                    <strong>⏰ {timeKey}</strong>
                    <div className="small text-muted">
                      {grouped[timeKey].length} volunteer shift
                      {grouped[timeKey].length !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="row g-4">
                    {grouped[timeKey]
                      .sort((a, b) =>
                        String(a?.activityTitle ?? "").localeCompare(
                          String(b?.activityTitle ?? "")
                        )
                      )
                      .map((slot) => {
                        const assignedCount = (slot.assignedUsers || []).length;
                        const spotsRemaining =
                          Math.max(Number(slot.volunteersNeeded || 0) - assignedCount, 0);
                        const isFilled = spotsRemaining === 0;

                        return (
                          <div
                            key={`${slot.activityId}-${slot.slotId}`}
                            className="col-12 col-md-6"
                          >
                            <div className="card h-100 border shadow-sm">
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                                  <div>
                                    <h5 className="mb-1">
                                      {slot.activityTitle || "Volunteer Shift"}
                                    </h5>

                                    <div className="text-muted small">
                                      {formatDateTime(slot.startTime)} –{" "}
                                      {formatDateTime(slot.endTime)}
                                    </div>
                                  </div>

                                  <span className={`badge ${isFilled ? "bg-success" : "bg-primary"}`}>
                                    {isFilled ? "Filled" : `${spotsRemaining} open`}
                                  </span>
                                </div>

                                <div className="d-flex flex-wrap gap-2 mb-3">
                                  <span className="badge bg-secondary">
                                    {assignedCount} assigned
                                  </span>

                                  <span className="badge bg-info text-dark">
                                    {slot.volunteersNeeded} needed
                                  </span>
                                </div>

                                {isFilled ? (
                                  <div className="small text-success fw-bold">
                                    This volunteer role is filled.
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleSignup(slot)}
                                    disabled={submitting}
                                  >
                                    {submitting ? "Signing Up..." : "🙋 Sign Up"}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );

};

export default VolunteerOpportunities;
